/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Locale } from "@i18n/react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  numericPercentages,
  spacing,
  ThemeProvider,
} from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import {
  getLanguageTriggerLabel,
  LANGUAGE_OPTIONS,
} from "../../../../lib/constants/languageOptions.constants";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import { Globe1Icon } from "../../../_base/icons/Icon/GeneralIcons";
import { ChevronDownIcon } from "../../../_base/icons/Icon/NavigationIcons";
import { ModalDropdownMenu } from "../../../_base/modals/ModalDropdownMenu/ModalDropdownMenu.emotion";
import { ModalDropdownMenuHeader } from "../../../_base/modals/ModalDropdownMenuHeader/ModalDropdownMenuHeader.emotion";
import { ModalDropdownMenuItem } from "../../../_base/modals/ModalDropdownMenuItem/ModalDropdownMenuItem.emotion";
import { ScrollArea } from "../ScrollArea/ScrollArea.emotion";

const listStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  rowGap: spacing.xxs,
};
const labelStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  rowGap: spacing.t,
};
const rowStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  width: numericPercentages[100],
};
const scrollStyle = (isDesktop: boolean): Interpolation => ({
  maxHeight: isDesktop ? "75dvh" : "60dvh",
  width: numericPercentages[100],
});
const subtitleStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});

export type LanguagePickerProps = {
  currentLocale: Locale;
  onOpen?: () => void;
  onSelect: (locale: Locale) => void;
  title: string;
  variant?: "compact" | "full";
};

export const LanguagePicker = ({
  currentLocale,
  onOpen,
  onSelect,
  title,
  variant = "compact",
}: LanguagePickerProps) => {
  const isDesktop = useIsDesktopViewport();
  const triggerLabel = getLanguageTriggerLabel({
    locale: currentLocale,
    variant,
  });

  const trigger = (
    <Button
      aria-label={`${title}: ${triggerLabel}`}
      endIcon={<ChevronDownIcon size={16} />}
      size="s"
      startIcon={<Globe1Icon size={20} />}
      variant={variant === "full" ? "lighten" : "transparentWhite"}
    >
      {triggerLabel}
    </Button>
  );

  return (
    <ThemeProvider as={null} forceTheme={isDesktop ? "dark" : "light"} scoped>
      <ModalDropdownMenu
        modalProps={{ hasMobilePulldownBar: true, title }}
        onOpenChange={(newOpen) => newOpen && onOpen?.()}
        trigger={trigger}
      >
        {!isDesktop && (
          <ModalDropdownMenuHeader>{title}</ModalDropdownMenuHeader>
        )}
        <ScrollArea
          horizontal={false}
          rootDisplayAsBlock
          viewportCSS={scrollStyle(isDesktop)}
        >
          <div css={listStyle}>
            {LANGUAGE_OPTIONS.map((option) => {
              const selected = option.locale === currentLocale;
              const currentProps = selected
                ? { "aria-current": "true" as const }
                : {};

              return (
                <ModalDropdownMenuItem
                  key={option.locale}
                  dropdownMenuItemProps={currentProps}
                  modalItemProps={currentProps}
                  onSelect={() => onSelect(option.locale)}
                  selected={selected}
                >
                  <span css={rowStyle}>
                    <span css={labelStyle}>
                      <Text
                        lang={option.locale}
                        size={isDesktop ? "s" : "m"}
                        type="title"
                      >
                        {option.endonym}
                      </Text>
                      {option.englishName && (
                        <Text css={subtitleStyle} size="xl" type="detail">
                          {option.englishName}
                        </Text>
                      )}
                    </span>
                  </span>
                </ModalDropdownMenuItem>
              );
            })}
          </div>
        </ScrollArea>
      </ModalDropdownMenu>
    </ThemeProvider>
  );
};
